import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import {catchError, Observable, switchMap, throwError} from 'rxjs';
import {AppNotificationService} from "../services/notification.service";
import { Router } from '@angular/router';
import { TokenStorageService } from 'src/app/authentication/storages/token-storage.service';
import { TranslocoService } from '@jsverse/transloco';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshQueue = [];

  constructor(
    private appnotificationService: AppNotificationService,
    private router: Router,
    private tokenStoreService: TokenStorageService,
    private translocoService: TranslocoService
    ) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.log("error in Error Interceptor", error ?? 'No Error');
          let errorMsg = '';
          if(error.status === 401){
            if(this.tokenStoreService.getToken() != null){
              return this.handleTokenExpired(request, next, error);
            }
            // Not logged in (e.g. failed login): no notification here, the caller decides what to show.
            return throwError(() => error);
          }
          else {
            errorMsg = this.handleBusinessErrors(error)
          }
          
          this.isRefreshing = false;
          console.log(errorMsg);
          return throwError(() => errorMsg);
        })
      )
  }

  private handleBusinessErrors (error: HttpErrorResponse) : string{

    let errorMsg = '';
    if (error.error instanceof ErrorEvent) {
      console.log(error, "error of type ErrorEvent");
      this.appnotificationService.notification$.next({message: error.error.message, style: 'error'})
      errorMsg = `Error: ${error.error.message}`;
    } 
    // Handle Backend Validation Errors
    else if(error.error?.errors){
      var errorMessages = error.error.errors;
      const keys = Object.keys(errorMessages);
      keys.forEach((key, index) => {
        console.log(`${key}: ${errorMessages[key][0]}`);
        this.appnotificationService.notification$.next({message: errorMessages[key][0], style: 'error'})
        errorMsg = `Error Code: ${error.status},  Message: ${errorMessages[key][0]}`;
      });
    }
    else if(error.error?.exception){
      this.appnotificationService.notification$.next({message: error.error.exception, style: 'error'})
      errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
    }
    else if(error.error?.messages){
      error.error.messages.forEach(message => {
        this.appnotificationService.notification$.next({message: message, style: 'error'})
        errorMsg = `Error Code: ${error.status},  Message: ${message}`;
      })
    }
    else {
      console.log(error, "general Error");
      this.appnotificationService.notification$.next({message: this.translocoService.translate('errors.unexpected'), style: 'error'})
      errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
    }

    return errorMsg;
  }

  private handleTokenExpired(request: HttpRequest<any>, next: HttpHandler,error: HttpErrorResponse): Observable<HttpEvent<any>> {
    // Call the refresh token endpoint to get a new access token
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      return this.tokenStoreService.refreshAccessToken().pipe(
        switchMap((token : string) => {
          // Retry the original request with the new access token
          this.isRefreshing = false;
          this.refreshQueue.forEach((req) => {
            const newRequest = this.addTokenHeader(req.request, token);
            req.next.handle(newRequest).subscribe({
              next: (res) => req.subscriber.next(res),
              error: (err) => req.subscriber.error(err),
              complete: () => req.subscriber.complete()
            });
          });

          this.refreshQueue = [];
          const newRequest = this.addTokenHeader(request, token);
          return next.handle(newRequest);
        }),
        catchError((error) => {
          // Handle refresh token error (e.g., redirect to login page)
          console.error('Error handling expired access token:', error);
          this.tokenStoreService.signOut();
          this.refreshQueue.forEach((req) => req.subscriber.error(error));
          this.refreshQueue = [];
          // Full reload (not a soft navigation) so no previous user's in-memory state survives.
          window.location.href = '/auth';
          return throwError(() => error);
        })
      );
    } 
    else if(request.url.includes('/tokens/refresh')){
      return throwError(() => "refresh token failed");
    }
    else {
        return new Observable<HttpEvent<any>>((subscriber) => {
            this.refreshQueue.push({ request, next, subscriber, error });
        });
    }
    
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone(
      { 
        headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) 
      });
  }
}
